import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const UserMenu = ({ userRole, isOpen, onToggle, className = '' }) => {
  const location = useLocation();

  // Define menu items based on user roles
  const getMenuItemsByRole = (role) => {
    const baseItems = [
      {
        id: 'dashboard',
        label: 'Tableau de bord',
        path: '/partner-dashboard',
        icon: 'LayoutDashboard',
        roles: ['partner', 'admin', 'agent', 'artist', 'venue'],
        tooltip: 'Vue d\'ensemble des performances et métriques'
      },
      {
        id: 'search',
        label: 'Recherche',
        path: '/advanced-search',
        icon: 'Search',
        roles: ['partner', 'admin', 'agent', 'artist', 'venue'],
        tooltip: 'Rechercher des artistes, agents ou venues'
      },
      {
        id: 'messaging',
        label: 'Messagerie',
        path: '/private-messaging',
        icon: 'MessageCircle',
        roles: ['partner', 'admin', 'agent', 'artist', 'venue'],
        tooltip: 'Messages privés et communications'
      },
      {
        id: 'tracking',
        label: 'Liens de suivi',
        path: '/link-analytics',
        icon: 'Link',
        roles: ['partner', 'admin', 'agent'],
        tooltip: 'Analytiques des liens de suivi'
      },
      {
        id: 'link-generator',
        label: 'Générateur de liens',
        path: '/link-generator',
        icon: 'Plus',
        roles: ['partner', 'admin', 'agent'],
        tooltip: 'Créer des liens de suivi personnalisés'
      },
      {
        id: 'commissions',
        label: 'Commissions',
        path: '/commission-tracking',
        icon: 'DollarSign',
        roles: ['partner', 'admin', 'agent'],
        tooltip: 'Suivi des revenus et paiements'
      },
      {
        id: 'profile',
        label: 'Mon Profil',
        path: role === 'artist' ? '/enhanced-artist-profile' : '/partner-profile',
        icon: 'User',
        roles: ['partner', 'admin', 'agent', 'artist', 'venue'],
        tooltip: 'Gestion du profil et paramètres'
      },
      {
        id: 'help',
        label: 'Aide',
        path: '/help-documentation',
        icon: 'HelpCircle',
        roles: ['partner', 'admin', 'agent', 'artist', 'venue'],
        tooltip: 'Documentation et support'
      },
      {
        id: 'admin',
        label: 'Administration',
        path: '/admin-dashboard',
        icon: 'Settings',
        roles: ['admin'],
        tooltip: 'Gestion système et partenaires'
      },
      {
        id: 'roadmap',
        label: 'Feuille de route',
        path: '/admin-roadmap',
        icon: 'Map',
        roles: ['admin'],
        tooltip: 'Planification et développement'
      }
    ];

    return baseItems?.filter(item => item?.roles?.includes(role));
  };

  // Group menu items by category for better organization
  const getGroupedMenuItems = (role) => {
    const allItems = getMenuItemsByRole(role);
    
    const groups = {
      main: {
        title: 'Principal',
        items: allItems?.filter(item => ['dashboard', 'profile']?.includes(item?.id))
      },
      business: {
        title: 'Business',
        items: allItems?.filter(item => ['search', 'messaging', 'commissions']?.includes(item?.id))
      },
      tools: {
        title: 'Outils',
        items: allItems?.filter(item => ['tracking', 'link-generator']?.includes(item?.id))
      },
      support: {
        title: 'Support',
        items: allItems?.filter(item => ['help']?.includes(item?.id))
      },
      admin: {
        title: 'Administration',
        items: allItems?.filter(item => ['admin', 'roadmap']?.includes(item?.id))
      }
    };

    // Filter out empty groups
    return Object?.entries(groups)?.filter(([key, group]) => group?.items?.length > 0);
  };

  const groupedItems = getGroupedMenuItems(userRole);

  // Get role-specific title
  const getRoleTitle = (role) => {
    const roleTitles = {
      partner: 'Menu Partenaire',
      admin: 'Menu Administrateur',
      agent: 'Menu Agent',
      artist: 'Menu Artiste',
      venue: 'Menu Venue'
    };
    return roleTitles?.[role] || 'Menu Utilisateur';
  };

  // Get role-specific badge color
  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-red-100 text-red-800 border-red-200',
      partner: 'bg-blue-100 text-blue-800 border-blue-200',
      agent: 'bg-green-100 text-green-800 border-green-200',
      artist: 'bg-purple-100 text-purple-800 border-purple-200',
      venue: 'bg-orange-100 text-orange-800 border-orange-200'
    };
    return colors?.[role] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  if (!isOpen) return null;

  return (
    <div className={`bg-card border border-border/80 rounded-lg shadow-xl overflow-hidden ${className}`}>
      {/* Header */}
      <div className="px-4 py-3 bg-muted border-b border-border/80">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-foreground">{getRoleTitle(userRole)}</h3>
          <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getRoleBadgeColor(userRole)}`}>
            {userRole?.toUpperCase()}
          </span>
        </div>
      </div>

      {/* Menu Groups */}
      <div className="py-2 max-h-96 overflow-y-auto bg-card">
        {groupedItems?.map(([groupKey, group], groupIndex) => (
          <div key={groupKey} className={groupIndex > 0 ? 'border-t border-border mt-2 pt-2' : ''}>
            {group?.title && group?.items?.length > 1 && (
              <div className="px-4 py-2">
                <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {group?.title}
                </h4>
              </div>
            )}
            <nav className="space-y-1 px-2">
              {group?.items?.map((item) => (
                <Link
                  key={item?.id}
                  to={item?.path}
                  className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 ${
                    location?.pathname === item?.path
                      ? 'text-accent bg-accent/15 border border-accent/30' :'text-foreground hover:text-accent hover:bg-muted'
                  }`}
                  title={item?.tooltip}
                  onClick={onToggle}
                >
                  <Icon name={item?.icon} size={16} />
                  <span>{item?.label}</span>
                  {location?.pathname === item?.path && (
                    <div className="ml-auto w-2 h-2 bg-accent rounded-full"></div>
                  )}
                </Link>
              ))}
            </nav>
          </div>
        ))}
      </div>

      {/* Quick Stats (for partners/agents) */}
      {(['partner', 'agent']?.includes(userRole)) && (
        <div className="px-4 py-3 bg-muted border-t border-border/80">
          <div className="grid grid-cols-2 gap-4 text-center">
            <div className="space-y-1">
              <div className="text-lg font-semibold text-foreground">12</div>
              <div className="text-xs text-muted-foreground">Liens actifs</div>
            </div>
            <div className="space-y-1">
              <div className="text-lg font-semibold text-foreground">€2,847</div>
              <div className="text-xs text-muted-foreground">Ce mois</div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions for Artists */}
      {userRole === 'artist' && (
        <div className="px-4 py-3 bg-muted border-t border-border/80">
          <div className="space-y-2">
            <Button variant="outline" size="sm" className="w-full justify-start">
              <Icon name="Calendar" size={14} className="mr-2" />
              Disponibilités
            </Button>
            <Button variant="outline" size="sm" className="w-full justify-start">
              <Icon name="Upload" size={14} className="mr-2" />
              Media Gallery
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMenu;